import { ChevronRight } from 'lucide-react'
import { CapText } from '@/features/client-app-preview/components/cap-text'
import { FolderIcon } from '@/features/client-app-preview/components/icons/folder-icon'
import { cn } from '@/shared/lib/utils'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible'

export type PortfolioSectionProps = {
  name: string
  count: number
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Atenúa el ícono: lo usa la sección de heredados, que no se cura desde acá. */
  muted?: boolean
  children: React.ReactNode
}

/**
 * UNA CATEGORÍA DEL CARTAPACIO, como sección plegable.
 *
 * ## Por qué es plegable y no una carpeta en la que se entra
 *
 * En la app se ENTRA a la carpeta, y ahí está bien: el teléfono tiene la pantalla entera y una
 * pila de navegación. Acá esto vive dentro de una pestaña, dentro de un panel lateral de 320 a
 * 600 px, que a su vez vive dentro de una pantalla — sumarle un nivel de navegación propio deja
 * al curador con dos «atrás» distintos y sin saber cuál lo saca de dónde.
 *
 * Plegar resuelve lo mismo que resolvía entrar —que «Planos» no deje a «Legales» a seis pantallas
 * de scroll— sin gastar un nivel: se ve el índice completo de un vistazo y se abre lo que se va a
 * tocar. El vocabulario visual sí es el de la app: el ícono de carpeta, el nombre en 16 semiBold y
 * el conteo abajo, igual que `CapFolderRow`.
 *
 * ## El conteo es el de lo FILTRADO
 *
 * Quien lo arma ya aplicó el buscador. Un «Planos 12» sobre tres tarjetas visibles obliga a
 * preguntarse dónde están las otras nueve, que es justo la duda que un buscador tiene que borrar.
 *
 * ## La flecha gira, y es lo único que se anima
 *
 * 150 ms `ease-out` sobre `transform`. Es una lista que un curador abre y cierra decenas de veces
 * por día: cualquier cosa más larga se siente lenta, y animar la ALTURA del contenido —que es lo
 * que la tentación pide— es animar `height`, que repinta y que en una grilla de doce tarjetas con
 * imágenes se nota. El contenido aparece; la flecha explica que fue a propósito.
 */
export function PortfolioSection({
  name,
  count,
  open,
  onOpenChange,
  muted = false,
  children
}: PortfolioSectionProps) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="w-full">
      <CollapsibleTrigger className="flex w-full flex-row items-center gap-3 py-2 text-left">
        <div
          className={cn(
            'cap-bg-surface-cover flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--cap-radius-sm)]',
            muted && 'opacity-60'
          )}
        >
          <FolderIcon size={20} />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <CapText variant="heading" weight="semiBold" tone="soft" truncate>
            {name}
          </CapText>
          <CapText variant="caption" tone="muted" tabular>
            {count === 1 ? '1 documento' : `${count} documentos`}
          </CapText>
        </div>

        <ChevronRight
          aria-hidden
          className={cn(
            'cap-fg-neutral-400 h-4 w-4 shrink-0 transition-transform duration-150 ease-out',
            open && 'rotate-90'
          )}
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="pt-1 pb-4">{children}</CollapsibleContent>
    </Collapsible>
  )
}
